import Pagination from '../../components/Common/Pagination';
import { TableLoader } from '../../components/Common/Loader';
import React, { useState, useMemo, useEffect } from 'react';
import { ArrowLeft, Eye, Edit2, X, Shield, ShieldCheck, Check, RefreshCw } from 'lucide-react';
import type { User } from '../../constants/mockData';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchUsersList,
  createUserThunk,
  updateUserThunk,
} from '../../store/usersSlice';
import { UserForm } from './UserForm';
import CSVButton from '../../components/Common/CSVButton';
import { exportToCSV } from '../../utils/csvExport';
import { getRolesList, quickAssignUserRoles } from '../../api/rbac';
import type { Role } from '../../types/rbac';

export const UsersList: React.FC = () => {
  const dispatch = useAppDispatch();
  const users = useAppSelector((state) => state.users.users);
  const isLoading = useAppSelector((state) => state.users.loading);
  const error = useAppSelector((state) => state.users.error);

  const [activeTab, setActiveTab] = useState<'Active' | 'Inactive' | 'All'>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRoleFilter, setSelectedRoleFilter] = useState('');
  const [entriesLimit, setEntriesLimit] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [view, setView] = useState<'list' | 'add' | 'edit'>('list');
  const [editingUser, setEditingUser] = useState<User | any | null>(null);
  const [viewingUser, setViewingUser] = useState<any | null>(null);

  // Quick Role Assignment Modal State
  const [rolesList, setRolesList] = useState<Role[]>([]);
  const [quickRoleUser, setQuickRoleUser] = useState<any | null>(null);
  const [quickSelectedRoleIds, setQuickSelectedRoleIds] = useState<number[]>([]);
  const [savingQuickRoles, setSavingQuickRoles] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  // Fetch users & system roles on mount
  useEffect(() => {
    dispatch(fetchUsersList());
    getRolesList()
      .then((res) => {
        if (res && res.data) setRolesList(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {});
  }, [dispatch]);

  // Reset currentPage to 1 when filters or entries limit change
  useEffect(() => {
    queueMicrotask(() => setCurrentPage(1));
  }, [searchQuery, activeTab, selectedRoleFilter, entriesLimit]);

  // Map backend raw User properties to UI fields
  const uiUsers = useMemo(() => {
    return users.map((u: any) => {
      const rawRoles = Array.isArray(u.roles)
        ? u.roles
        : Array.isArray(u.roleIds)
        ? u.roleIds.map((id: number) => {
            const match = rolesList.find((r) => r.id === id);
            return { id, name: match ? match.name : `Role #${id}` };
          })
        : u.role
        ? typeof u.role === 'object'
          ? [u.role]
          : [{ id: 0, name: u.role }]
        : [];

      const primaryRole =
        typeof u.role === 'object'
          ? u.role?.name
          : u.role || (rawRoles[0] ? rawRoles[0].name : u.designation || 'USER');

      return {
        id: u.id,
        username: u.userId || u.username || `user_${u.id}`,
        name: u.name || '',
        employeeId: u.employeeId || '',
        designation: u.designation || '',
        phoneNo: u.phone || u.phoneNo || '',
        email: u.email || '',
        status: (u.status?.toLowerCase() === 'active' ? 'Active' : 'Inactive') as 'Active' | 'Inactive',
        role: primaryRole,
        roles: rawRoles,
        roleIds: Array.isArray(u.roleIds)
          ? u.roleIds
          : rawRoles.map((r: any) => r.id).filter(Boolean),
        forRegion: u.scopes?.[0]?.cityName || u.scopes?.[0]?.areaName || '',
        zone: u.scopes?.[0]?.zoneName || '',
      };
    });
  }, [users, rolesList]);

  // Derived Counts
  const activeCounts = useMemo(() => uiUsers.filter((u) => u.status === 'Active').length, [uiUsers]);
  const inactiveCounts = useMemo(() => uiUsers.filter((u) => u.status === 'Inactive').length, [uiUsers]);

  // Filtering Logic
  const filteredUsers = useMemo(() => {
    return uiUsers.filter((user) => {
      // Tab filter
      if (activeTab !== 'All' && user.status !== activeTab) {
        return false;
      }
      // Role filter
      if (selectedRoleFilter) {
        const hasMatchingRole = user.roles.some(
          (r: any) => r.name?.toLowerCase() === selectedRoleFilter.toLowerCase()
        );
        if (!hasMatchingRole && user.role?.toLowerCase() !== selectedRoleFilter.toLowerCase()) {
          return false;
        }
      }
      // Search filter
      const searchLower = searchQuery.toLowerCase();
      return (
        user.name.toLowerCase().includes(searchLower) ||
        user.username.toLowerCase().includes(searchLower) ||
        user.employeeId.toLowerCase().includes(searchLower) ||
        user.designation.toLowerCase().includes(searchLower) ||
        user.phoneNo.includes(searchLower) ||
        user.role.toLowerCase().includes(searchLower) ||
        user.forRegion.toLowerCase().includes(searchLower) ||
        user.zone.toLowerCase().includes(searchLower)
      );
    });
  }, [uiUsers, activeTab, selectedRoleFilter, searchQuery]);

  // Paginated Users
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * entriesLimit;
    return filteredUsers.slice(startIndex, startIndex + entriesLimit);
  }, [filteredUsers, currentPage, entriesLimit]);

  const handleOpenAddModal = () => {
    setEditingUser(null);
    setView('add');
  };

  // Handle Add/Edit User Submit
  const handleUserSubmit = (formData: any) => {
    if (editingUser) {
      const payload = {
        id: editingUser.id,
        name: formData.name,
        userId: formData.username,
        password: formData.password || undefined,
        roleName: formData.role || 'USER',
        roleIds: formData.roleIds || [],
        email: formData.email || '',
        phone: formData.phoneNo,
        employeeId: formData.employeeId,
        designation: formData.designation,
        dateOfBirth: formData.dob || '',
        imei: formData.imei || '',
        address: formData.address || '',
        status: formData.status === 'Active' ? 'ACTIVE' : 'INACTIVE',
      };
      dispatch(updateUserThunk(payload));
      showToast('User details and role assignments updated successfully');
    } else {
      const payload = {
        name: formData.name,
        userId: formData.username,
        password: formData.password || 'password123',
        roleName: formData.role || 'USER',
        roleIds: formData.roleIds || [],
        email: formData.email || '',
        phone: formData.phoneNo,
        employeeId: formData.employeeId,
        designation: formData.designation,
        dateOfBirth: formData.dob || '',
        imei: formData.imei || '',
        address: formData.address || '',
        status: formData.status === 'Active' ? 'ACTIVE' : 'INACTIVE',
      };
      dispatch(createUserThunk(payload));
      showToast('New multi-role user created successfully');
    }

    setView('list');
  };

  // Quick Role Modal Handler
  const handleOpenQuickRoles = (user: any) => {
    setQuickRoleUser(user);
    const existingIds = user.roleIds || user.roles.map((r: any) => r.id).filter(Boolean);
    setQuickSelectedRoleIds(existingIds);
  };

  const handleToggleQuickRole = (roleId: number) => {
    setQuickSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
  };

  const handleSaveQuickRoles = async () => {
    if (!quickRoleUser) return;
    try {
      setSavingQuickRoles(true);
      await quickAssignUserRoles({
        userId: quickRoleUser.id,
        roleIds: quickSelectedRoleIds,
      });
      showToast(`Roles updated for user "${quickRoleUser.name}"`);
      setQuickRoleUser(null);
      dispatch(fetchUsersList());
    } catch (err: any) {
      showToast(err.message || 'Failed to update roles');
    } finally {
      setSavingQuickRoles(false);
    }
  };

  // CSV Download Action
  const handleDownloadCSV = () => {
    const headers = ['User ID', 'User Name', 'Name', 'Phone No', 'Status', 'Primary Role', 'All Assigned Roles', 'Zone'];
    const rows = filteredUsers.map((user) => [
      user.id,
      user.username,
      user.name,
      user.phoneNo,
      user.status,
      user.role,
      user.roles.map((r: any) => r.name).join(', '),
      user.zone,
    ]);

    exportToCSV(`gowardhan_users_${activeTab.toLowerCase()}`, headers, rows);
  };

  if (view === 'add' || view === 'edit') {
    return (
      <UserForm
        isEdit={view === 'edit'}
        initialData={editingUser || undefined}
        onSubmit={handleUserSubmit}
        onCancel={() => setView('list')}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toastMessage && (
        <div className="fixed top-5 right-5 z-50 px-4 py-3 rounded-xl bg-indigo-900 text-white text-sm font-semibold shadow-2xl flex items-center gap-2 border border-indigo-700 animate-slide-in">
          <Check className="w-4 h-4 text-emerald-400" />
          {toastMessage}
        </div>
      )}

      {/* Dashboard Sub-Header / Control Row */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
            title="Go Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight m-0">
              User Multi-Role Directory
            </h1>
            <p className="text-xs text-slate-500">Manage user access levels, primary roles, and multi-role assignments</p>
          </div>
        </div>

        {/* Status Filter Tabs */}
        <div className="flex bg-white p-1 rounded-full border border-slate-200 w-fit">
          {(['All', 'Active', 'Inactive'] as const).map((tab) => {
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-1.5 rounded-full text-xs font-bold transition-all duration-150 cursor-pointer ${
                  isActive
                    ? 'go_erp-bg text-white shadow-sm'
                    : 'text-slate-650 hover:text-slate-900'
                }`}
              >
                {tab}
              </button>
            );
          })}
        </div>

        {/* Right buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleOpenAddModal}
            className="go_erp-bg text-white text-sm px-4 py-1.5 rounded-xl flex items-center gap-2 cursor-pointer transition-colors shadow-sm font-semibold"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add User
          </button>

          <CSVButton onClick={handleDownloadCSV} />
        </div>
      </div>

      {/* Counts Header */}
      <div className="flex items-center justify-end gap-4 text-xs font-bold text-slate-800">
        <span>Active Counts: <span className="font-extrabold text-emerald-600">{activeCounts}</span></span>
        <span>Inactive Counts: <span className="font-extrabold text-slate-400">{inactiveCounts}</span></span>
      </div>

      {/* Main Table Card */}
      <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
        {/* Table Toolbar */}
        <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-slate-50/50 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-500">Show</span>
            <select
              value={entriesLimit}
              onChange={(e) => setEntriesLimit(Number(e.target.value))}
              className="bg-white border border-slate-200 text-xs font-bold text-slate-700 rounded px-2 py-1 outline-none focus:border-slate-400"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="text-xs font-semibold text-slate-500">entries</span>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Filter by Role */}
            <select
              value={selectedRoleFilter}
              onChange={(e) => setSelectedRoleFilter(e.target.value)}
              className="bg-white border border-slate-200 text-xs font-semibold text-slate-700 rounded-lg px-3 py-1.5 outline-none focus:border-indigo-500"
            >
              <option value="">All Roles</option>
              {rolesList.map((r) => (
                <option key={r.id} value={r.name}>
                  {r.name}
                </option>
              ))}
            </select>

            {/* Embedded table search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search user..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-white border border-slate-200 text-xs rounded-full py-1.5 pl-8 pr-3 outline-none focus:border-indigo-500 w-48 transition-all"
              />
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
            </div>
          </div>
        </div>

        {/* Table Wrapper */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#FFF8E1]">
                <th className="px-4 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-wider">User ID</th>
                <th className="px-4 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-wider">User Name</th>
                <th className="px-4 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Name</th>
                <th className="px-4 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Primary Role</th>
                <th className="px-4 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Assigned Roles</th>
                <th className="px-4 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Phone No</th>
                <th className="px-4 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#ECECF2]">
              {isLoading ? (
                <TableLoader colSpan={8} text="Loading users..." />
              ) : error ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-xs font-bold text-rose-500">
                    Error: {error}
                  </td>
                </tr>
              ) : paginatedUsers.length > 0 ? (
                paginatedUsers.map((user) => {
                  const assignedRoles: any[] = user.roles || [];
                  const extraRolesCount = Math.max(0, assignedRoles.length - 2);

                  return (
                    <tr key={user.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-4 text-xs font-bold text-slate-700">#{user.id}</td>
                      <td className="px-4 py-4 text-xs font-semibold text-slate-600">{user.username}</td>
                      <td className="px-4 py-4 text-xs font-bold text-slate-900">{user.name}</td>
                      
                      {/* Primary Role Chip */}
                      <td className="px-4 py-4 text-xs">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-indigo-50 text-indigo-700 font-bold border border-indigo-200">
                          <ShieldCheck className="w-3 h-3 text-indigo-500" />
                          {user.role}
                        </span>
                      </td>

                      {/* Multi-Role Tags Chips */}
                      <td className="px-4 py-4 text-xs">
                        <div className="flex flex-wrap items-center gap-1 max-w-xs">
                          {assignedRoles.length === 0 ? (
                            <span className="text-slate-400 italic text-[11px]">No extra roles</span>
                          ) : (
                            <>
                              {assignedRoles.slice(0, 2).map((r: any, idx: number) => (
                                <span
                                  key={idx}
                                  className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-medium text-[11px] border border-slate-200"
                                >
                                  {r.name}
                                </span>
                              ))}
                              {extraRolesCount > 0 && (
                                <span
                                  className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 font-bold text-[10px] cursor-help"
                                  title={assignedRoles.slice(2).map((r: any) => r.name).join(', ')}
                                >
                                  +{extraRolesCount} more
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </td>

                      <td className="px-4 py-4 text-xs font-semibold text-slate-650">{user.phoneNo || '—'}</td>
                      
                      {/* Status badge */}
                      <td className="px-4 py-4 text-xs">
                        <span
                          className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${
                            user.status === 'Active'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-slate-100 text-slate-500'
                          }`}
                        >
                          {user.status}
                        </span>
                      </td>

                      {/* Action buttons */}
                      <td className="px-4 py-4 text-xs text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleOpenQuickRoles(user)}
                            className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="Quick Assign Roles"
                          >
                            <Shield className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setViewingUser(user)}
                            className="p-1.5 text-slate-500 hover:text-[#B71C1C] transition-colors rounded-lg"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setEditingUser(user);
                              setView('edit');
                            }}
                            className="p-1.5 text-slate-500 hover:text-blue-600 transition-colors rounded-lg"
                            title="Edit User"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8} className="px-6 py-10 text-center text-xs font-semibold text-slate-400">
                    No users found matching filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Table Footer */}
        <Pagination
          currentPage={currentPage}
          totalEntries={filteredUsers.length}
          entriesLimit={entriesLimit}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* Quick Role Assignment Modal */}
      {quickRoleUser && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-200 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div>
                <h3 className="text-base font-bold text-slate-900">Quick Assign Roles</h3>
                <p className="text-xs text-slate-500">{quickRoleUser.name} (#{quickRoleUser.id})</p>
              </div>
              <button
                onClick={() => setQuickRoleUser(null)}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-2 max-h-60 overflow-y-auto">
              {rolesList.map((r) => {
                const isChecked = quickSelectedRoleIds.includes(r.id);
                return (
                  <div
                    key={r.id}
                    onClick={() => handleToggleQuickRole(r.id)}
                    className={`p-3 rounded-xl border text-xs font-semibold cursor-pointer flex items-center justify-between transition ${
                      isChecked
                        ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                        : 'bg-white border-slate-200 hover:border-slate-300 text-slate-700'
                    }`}
                  >
                    <span>{r.name}</span>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => {}}
                      className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                    />
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setQuickRoleUser(null)}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveQuickRoles}
                disabled={savingQuickRoles}
                className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold shadow-md shadow-indigo-600/20 disabled:opacity-50 flex items-center gap-2"
              >
                {savingQuickRoles && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                Save Roles
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View User Modal */}
      {viewingUser && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl border border-slate-100 space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div>
                <h3 className="text-lg font-extrabold text-slate-800">{viewingUser.name}</h3>
                <p className="text-xs font-mono font-medium text-slate-400">ID: {viewingUser.id} &bull; {viewingUser.username}</p>
              </div>
              <button onClick={() => setViewingUser(null)} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center cursor-pointer transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">Full Name</span>
                  <p className="text-sm font-bold text-slate-900">{viewingUser.name}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">Primary Role</span>
                  <p className="text-sm font-bold text-slate-900">{viewingUser.role || '—'}</p>
                </div>
              </div>

              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-2">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">Assigned Roles</span>
                <div className="flex flex-wrap gap-1.5">
                  {viewingUser.roles && viewingUser.roles.length > 0 ? (
                    viewingUser.roles.map((r: any, idx: number) => (
                      <span key={idx} className="px-2.5 py-1 rounded-lg bg-indigo-100 text-indigo-800 font-bold text-xs">
                        {r.name}
                      </span>
                    ))
                  ) : (
                    <span className="text-slate-400 italic">None</span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">Phone</span>
                  <p className="text-sm font-bold text-slate-900">{viewingUser.phoneNo || '—'}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">Status</span>
                  <p className="text-sm font-bold text-slate-900">{viewingUser.status}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersList;
